import { fetchAuthSession } from '@aws-amplify/auth';

import { BACKEND_AGENTS_API } from '../env';

// Administración del Verified Query Repository (VQR) -- pantalla "Agentes →
// Consultas verificadas". Mismo API Gateway que los agentes (agents-admin,
// ver src/agents-admin/verified_queries.py). El backend NUNCA devuelve el
// vector de embedding -- el front solo maneja los campos de negocio.
const API_BASE = BACKEND_AGENTS_API;
const VQR_URL = `${API_BASE}/vqr`;
const VQR_CANDIDATES_URL = `${VQR_URL}/candidates`;

async function getAuthHeader() {
  try {
    const idToken = (await fetchAuthSession()).tokens?.idToken?.toString();
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  } catch (error) {
    console.error('VerifiedQueryService.getAuthHeader: unable to fetch session', error);
    return {};
  }
}

async function request(path, init = {}) {
  const authHeader = await getAuthHeader();
  const headers = {
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    ...authHeader
  };
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  let response;
  try {
    response = await fetch(url, { ...init, headers, mode: 'cors', cache: 'no-cache' });
  } catch (networkError) {
    throw new Error(`No se pudo contactar el servicio de verified queries. ${networkError.message}`);
  }

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let message = raw || `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(raw);
      message = parsed.error || parsed.message || message;
    } catch {
      /* texto plano */
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  if (response.status === 204) {
    return null;
  }
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// `semanticModelId` vacío/undefined se traduce a `null` explícito -- el
// backend lo trata como "verified query global" (ver
// verified_queries.py::_validate_semantic_model_ownership), distinto de
// "no tocar este campo" (ausente del payload). Solo en `update` interesa
// esa distinción entre "ausente" y "null" -- en `create` siempre se manda.
function buildPayload({ question, sqlQuery, description, semanticModelId, status } = {}, { partial = false } = {}) {
  const payload = {};
  if (!partial || question !== undefined) payload.question = question;
  if (!partial || sqlQuery !== undefined) payload.sql_query = sqlQuery;
  if (!partial || description !== undefined) payload.description = description || '';
  if (!partial || semanticModelId !== undefined) payload.semantic_model_id = semanticModelId || null;
  if (partial && status !== undefined) payload.status = status;
  return payload;
}

const VerifiedQueryService = {
  // Devuelve { items, nextCursor } -- misma forma que
  // AuditService.listInvocations. `nextCursor` es null cuando ya no quedan
  // páginas. Antes esto desenvolvía `verified_queries` y descartaba el
  // resto de la respuesta, así que el cursor del backend se habría
  // perdido en silencio.
  async list({ semanticModelId, status, limit, cursor } = {}) {
    const qs = new URLSearchParams();
    if (semanticModelId) {
      qs.set('semantic_model_id', semanticModelId);
    }
    if (status) {
      qs.set('status', status);
    }
    if (limit) {
      qs.set('limit', String(limit));
    }
    if (cursor) {
      qs.set('cursor', cursor);
    }
    const query = qs.toString();
    const data = await request(query ? `${VQR_URL}?${query}` : VQR_URL, { method: 'GET' });
    return {
      items: Array.isArray(data?.verified_queries) ? data.verified_queries : [],
      nextCursor: data?.next_cursor || null
    };
  },

  async get(queryId) {
    if (!queryId) {
      throw new Error('VerifiedQueryService.get: queryId es requerido');
    }
    return request(`${VQR_URL}/${encodeURIComponent(queryId)}`, { method: 'GET' });
  },

  async create(fields) {
    if (!fields?.question?.trim()) {
      throw new Error('La pregunta es requerida.');
    }
    if (!fields?.sqlQuery?.trim()) {
      throw new Error('El SQL verificado es requerido.');
    }
    return request(VQR_URL, {
      method: 'POST',
      body: JSON.stringify(buildPayload(fields))
    });
  },

  async update(queryId, fields) {
    if (!queryId) {
      throw new Error('VerifiedQueryService.update: queryId es requerido');
    }
    return request(`${VQR_URL}/${encodeURIComponent(queryId)}`, {
      method: 'PUT',
      body: JSON.stringify(buildPayload(fields, { partial: true }))
    });
  },

  async remove(queryId) {
    if (!queryId) {
      throw new Error('VerifiedQueryService.remove: queryId es requerido');
    }
    return request(`${VQR_URL}/${encodeURIComponent(queryId)}`, { method: 'DELETE' });
  },

  // Fase 8 (curación semi-automática): captura un candidato desde el
  // feedback explícito de un usuario en el chat ("marcar como respuesta
  // confiable" -- ver Chat.js::handleResponseFeedback). Requiere el
  // permiso `agents.run` (el mismo que ya hace falta para chatear), NO
  // `agents.manage` -- cualquier usuario del tenant puede llamarlo, no
  // solo un admin. Queda en PENDING_REVIEW hasta que un admin lo apruebe
  // en "Agentes → Consultas verificadas".
  async submitCandidate({ queryUuid, question, agentConfigId } = {}) {
    if (!queryUuid) {
      throw new Error('VerifiedQueryService.submitCandidate: queryUuid es requerido');
    }
    if (!question) {
      throw new Error('VerifiedQueryService.submitCandidate: question es requerido');
    }
    if (!agentConfigId) {
      throw new Error('VerifiedQueryService.submitCandidate: agentConfigId es requerido');
    }
    return request(VQR_CANDIDATES_URL, {
      method: 'POST',
      body: JSON.stringify({ query_uuid: queryUuid, question, agent_config_id: agentConfigId })
    });
  }
};

export default VerifiedQueryService;
